import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlags } from 'utils/featureFlags';
import { type ProspectOption, useProspect } from './useProspect';
import { mapCrmCompanyToProspectOption } from 'services/company-and-contacts/mappers';
import { useMemo } from 'react';
import { useCrmCompaniesByContactId } from 'pages/flex-data/flex-data-links/useCrmCompaniesByContactId';
import { useSearchCrmCompanies } from 'pages/flex-data/flex-data-links/useSearchCrmCompanies';
import type { CompositeEntityId } from 'services/company-and-contacts/mappers';

export const MIN_COMPANY_SEARCH_TERM_LENGTH = 3;

export function useCompanyOptions({ contactId, searchTerm }: { contactId?: string | CompositeEntityId; searchTerm?: string } = {}) {
  const featureFlags = useFeatureFlag([FeatureFlags.useVariablesBff, FeatureFlags.crmDealLinking]);
  const isUseVariablesBffFlagEnabled = featureFlags[FeatureFlags.useVariablesBff];
  const isCrmDealLinkingEnabled = isUseVariablesBffFlagEnabled && featureFlags[FeatureFlags.crmDealLinking];

  const hasContact = !!contactId;
  const isSearchTermUsable = !searchTerm || searchTerm.length >= MIN_COMPANY_SEARCH_TERM_LENGTH;

  const prospectList = useProspect({ enabled: !isCrmDealLinkingEnabled });
  const crmCompaniesByContact = useCrmCompaniesByContactId({
    contactId: contactId as CompositeEntityId,
    enabled: isCrmDealLinkingEnabled && hasContact,
  });
  const crmCompaniesSearch = useSearchCrmCompanies({
    searchTerm,
    enabled: isCrmDealLinkingEnabled && !hasContact && isSearchTermUsable,
  });
  const crmCompanyList = hasContact ? crmCompaniesByContact : crmCompaniesSearch;

  const prospectOptions = prospectList.options;
  const crmCompanyOptions = useMemo(
    () =>
      crmCompanyList.crmCompanies
        .map(mapCrmCompanyToProspectOption)
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'accent' })),
    [crmCompanyList.crmCompanies]
  );
  const crmCompanyIsLoading = crmCompanyList.isLoading;

  const prospectLoadOptions = prospectList.loadOptions;
  const companyLoadOptions = async (
    inputValue: string,
    callback: (options: ProspectOption[], matched: ProspectOption | undefined) => void
  ) => {
    const filteredOptions = crmCompanyOptions.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()));
    const matchedCompany =
      inputValue.trim().length > 0
        ? filteredOptions.find((o) => o.label.localeCompare(inputValue, undefined, { sensitivity: 'accent' }) === 0)
        : undefined;
    callback(filteredOptions, matchedCompany);

    return { options: filteredOptions, matchedProspect: matchedCompany };
  };

  return {
    options: isCrmDealLinkingEnabled ? crmCompanyOptions : prospectOptions,
    isLoading: isCrmDealLinkingEnabled ? crmCompanyIsLoading : prospectList.isLoading,
    loadOptions: isCrmDealLinkingEnabled ? companyLoadOptions : prospectLoadOptions,
  };
}
